import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ExecutionCycle, ExecutionCycleCardComponent } from '../execution-cycle-card/execution-cycle-card.component';

@Component({
  selector: 'app-execution-cycle-view',
  standalone: true,
  imports: [CommonModule, ExecutionCycleCardComponent, NzPaginationModule],
  templateUrl: './execution-cycle-view.component.html',
  styleUrl: './execution-cycle-view.component.scss'
})
export class ExecutionCycleViewComponent implements OnInit {
  @Input() executionCycles: ExecutionCycle[] = [];
  @Input() showPagination: boolean = true;
  @Input() pageSize: number = 15;
  /** When true, the parent controls pagination via totalItems; cycles are displayed as-is (already a single page). */
  @Input() serverSidePagination: boolean = false;
  /** Total item count from BE meta — used when serverSidePagination is true. */
  @Input() totalItems: number = 0;
  /** When true renders Bootstrap placeholder skeleton rows instead of real data. */
  @Input() isLoading: boolean = false;

  readonly skeletonRows = new Array(5);

  @Output() deleteSelected = new EventEmitter<string[]>();
  @Output() refresh = new EventEmitter<void>();
  @Output() filterClick = new EventEmitter<void>();
  @Output() viewDetails = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();

  selectedCycles: Set<string> = new Set();
  currentPage: number = 1;

  ngOnInit(): void {
    if (!this.serverSidePagination) {
      this.totalItems = this.executionCycles.length;
    }
  }

  get displayedCycles(): ExecutionCycle[] {
    // In server-side mode the parent already provides the current page of data.
    if (this.serverSidePagination || !this.showPagination) return this.executionCycles;

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.executionCycles.slice(startIndex, endIndex);
  }

  get selectedCount(): number {
    return this.selectedCycles.size;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get showingText(): string {
    return `Showing ${this.showingStart} to ${this.showingEnd} of ${this.totalItems} execution cycles`;
  }

  onCheckboxChange(event: { id: string; checked: boolean }): void {
    if (event.checked) {
      this.selectedCycles.add(event.id);
    } else {
      this.selectedCycles.delete(event.id);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedCycles.has(id);
  }

  onDeleteClick(): void {
    if (this.selectedCount > 0) {
      this.deleteSelected.emit(Array.from(this.selectedCycles));
      this.selectedCycles.clear();
    }
  }

  onRefreshClick(): void {
    this.refresh.emit();
  }

  onFilterClick(): void {
    this.filterClick.emit();
  }

  onViewDetails(cycleId: string): void {
    this.viewDetails.emit(cycleId);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.pageChange.emit(page);
  }

  // Helper to extract badge from cycle name (e.g., "Build_2025-11-18" -> extract version/tag)
  getBadgeText(cycle: ExecutionCycle): string | undefined {
    // You can customize this logic based on your naming convention
    // For now, returning undefined - add logic as needed
    return undefined;
  }
}
